// Shared WHAPK1 wire format used by the lobby and standalone landing pages.
export interface ApkTransferPayload {
  v: 1;
  pid: string;
  token?: string;
  ts: number;
  source?: string;
  sig: string;
}

const APK_TRANSFER_KEY_MATERIAL =
  "WHAPK1::webhall-9betin::2026-03-24::install-transfer";
export const APK_TRANSFER_CLOCK_SKEW_MS = 5 * 60 * 1000;
const APK_TRANSFER_VERSION = 1;
const APK_TRANSFER_FNV_OFFSET = 0x811c9dc5;
const APK_TRANSFER_FNV_PRIME = 0x01000193;

export const APK_TRANSFER_PREFIX = "WHAPK1:";
export const APK_TRANSFER_HASH_KEY = "apk_payload";
export const APK_TRANSFER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

let cachedKeyBytes: Uint8Array | null = null;
const getTextEncoder = (): TextEncoder => new TextEncoder();

const getTextDecoder = (): TextDecoder => new TextDecoder();

const getKeyBytes = (): Uint8Array => {
  if (!cachedKeyBytes) {
    cachedKeyBytes = getTextEncoder().encode(APK_TRANSFER_KEY_MATERIAL);
  }

  return cachedKeyBytes;
};

const encodeBase64 = (binary: string): string => {
  return btoa(binary);
};

const decodeBase64 = (value: string): string => {
  return atob(value);
};

const encodeBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return encodeBase64(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const decodeBase64Url = (value: string): Uint8Array => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const normalized = padded + "=".repeat((4 - (padded.length % 4)) % 4);
  const binary = decodeBase64(normalized);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const obfuscateBytes = (input: Uint8Array): Uint8Array => {
  const output = new Uint8Array(input.length);
  const keyBytes = getKeyBytes();

  for (let index = 0; index < input.length; index += 1) {
    const keyByte = keyBytes[index % keyBytes.length];
    const rollingMask = (index * 31 + 17) & 0xff;
    const mask = keyByte ^ rollingMask;
    output[index] = input[index] ^ mask;
  }

  return output;
};

const serializePayload = (payload: ApkTransferPayload): string => {
  const orderedPayload = {
    v: payload.v,
    pid: payload.pid,
    ...(payload.token ? { token: payload.token } : {}),
    ts: payload.ts,
    ...(payload.source ? { source: payload.source } : {}),
    sig: payload.sig,
  };

  return JSON.stringify(orderedPayload);
};

const buildSignatureSource = ({
  v,
  pid,
  token,
  ts,
}: Omit<ApkTransferPayload, "sig">): string => {
  return [v, pid, token || "", ts].join("|");
};

export const createPayloadSignature = ({
  v,
  pid,
  token,
  ts,
}: Omit<ApkTransferPayload, "sig">): string => {
  const sourceBytes = getTextEncoder().encode(buildSignatureSource({ v, pid, token, ts }));
  let hash = APK_TRANSFER_FNV_OFFSET;

  for (const byte of sourceBytes) {
    hash ^= byte;
    hash = Math.imul(hash, APK_TRANSFER_FNV_PRIME) >>> 0;
  }

  return hash.toString(16).padStart(8, "0");
};

const isValidPayload = (
  payload: any,
  nowMs: number,
): payload is ApkTransferPayload => {
  if (
    !payload ||
    payload.v !== APK_TRANSFER_VERSION ||
    typeof payload.pid !== "string" ||
    !payload.pid.trim()
  ) {
    return false;
  }

  if (payload.token !== undefined && typeof payload.token !== "string") {
    return false;
  }

  if (typeof payload.ts !== "number" || !Number.isFinite(payload.ts) || payload.ts <= 0) {
    return false;
  }

  if (payload.source !== undefined && typeof payload.source !== "string") {
    return false;
  }

  if (typeof payload.sig !== "string" || payload.sig.length !== 8) {
    return false;
  }

  const age = nowMs - payload.ts;
  if (age > APK_TRANSFER_MAX_AGE_MS) {
    return false;
  }

  if (age < -APK_TRANSFER_CLOCK_SKEW_MS) {
    return false;
  }

  const expectedSig = createPayloadSignature({
    v: payload.v,
    pid: payload.pid,
    token: payload.token,
    ts: payload.ts,
  });

  return expectedSig === payload.sig;
};

export const buildApkTransferPayload = (
  pid: string,
  token?: string,
  nowMs = Date.now(),
  source?: string,
): ApkTransferPayload => {
  const payloadBase = {
    v: 1 as const,
    pid,
    ts: nowMs,
    ...(token ? { token } : {}),
    ...(source ? { source } : {}),
  };

  return {
    ...payloadBase,
    sig: createPayloadSignature(payloadBase),
  };
};

export const encryptApkTransferPayload = (
  payload: ApkTransferPayload,
): string => {
  const plainBytes = getTextEncoder().encode(serializePayload(payload));
  const obfuscated = obfuscateBytes(plainBytes);
  return encodeBase64Url(obfuscated);
};

export const decryptApkTransferPayload = (
  cipherText: string,
  nowMs = Date.now(),
): ApkTransferPayload | null => {
  try {
    const obfuscated = decodeBase64Url(cipherText);
    if (!obfuscated.length) {
      return null;
    }
    const plainBytes = obfuscateBytes(obfuscated);
    const payload = JSON.parse(getTextDecoder().decode(plainBytes)) as ApkTransferPayload;

    if (!isValidPayload(payload, nowMs)) {
      return null;
    }

    return payload;
  } catch (_error) {
    return null;
  }
};

export const createApkTransferClipboardText = (
  pid: string,
  token?: string,
  nowMs = Date.now(),
  source?: string,
): string => {
  const payload = buildApkTransferPayload(pid, token, nowMs, source);
  const cipherText = encryptApkTransferPayload(payload);

  return `${APK_TRANSFER_PREFIX}${cipherText}`;
};
